import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  DashboardTab,
  DashboardFilters,
  FileFilters,
} from '../components/Dashboard/types/dashboard.types';
import {
  getInitialSelectedSerial,
  getInitialFileFilters,
  persistSelectedSerial,
  persistFileFilters,
} from './dashboardPersistence';
import { replaceUrlState, syncUrlState } from './dashboardUrlSync';
import type { FolderRef } from '../components/file-browser/types';
import { createDataActions } from './dashboardDataActions';
import { createEffectsActions } from './dashboardEffectsActions';
import { DashboardData, DashboardState, DashboardStore } from './dashboardStore.types';

export type {
  DashboardData,
  DashboardState,
  DashboardActions,
  DashboardStore,
} from './dashboardStore.types';

export const useDashboardStore = create<DashboardStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      selectedSerial: getInitialSelectedSerial(),
      sliderValues: [0, 100],
      fileFilters: getInitialFileFilters(),
      hasAttemptedLoad: false,
      activeTab: 'overview',
      filesFolderId: null,
      filesFolderInvalid: false,

      // Auth state (minimal)
      isLoggingOut: false,

      // Data state
      data: {
        flightTypes: [],
        serialNumbers: [],
        stats: null,
        droneStats: null,
        dateRange: null,
        allDronesSummary: null,
      },
      loading: false,
      updating: false,
      error: null,

      // Ref replacements
      prevSliderValues: [-1, -1],

      // Deduplication tracking
      ongoingRequests: new Set<string>(),

      // Filter actions
      setSelectedSerial: (serial: string) => {
        persistSelectedSerial(serial);
        set({ selectedSerial: serial }, false, 'setSelectedSerial');
        // Preserve current tab; clearing serial returns to overview
        const tab = serial ? get().activeTab : 'overview';
        syncUrlState(tab, serial, get().filesFolderId);
      },

      setSliderValues: (values: number[]) => {
        set({ sliderValues: values }, false, 'setSliderValues');
      },

      updateFilters: (filters: Partial<DashboardFilters>) => {
        const updates: Partial<DashboardState> = {};

        if (filters.selectedSerial !== undefined) {
          persistSelectedSerial(filters.selectedSerial);
          updates.selectedSerial = filters.selectedSerial;
        }
        if (filters.sliderValues !== undefined) {
          updates.sliderValues = filters.sliderValues;
        }
        if (filters.fileFilters !== undefined) {
          persistFileFilters(filters.fileFilters);
          updates.fileFilters = filters.fileFilters;
        }

        set(updates, false, 'updateFilters');

        // Sync URL when serial changes via updateFilters — preserve current tab
        if (filters.selectedSerial !== undefined) {
          const serial = filters.selectedSerial;
          const tab = serial ? get().activeTab : 'overview';
          syncUrlState(tab, serial, get().filesFolderId);
        }
      },

      setFileFilters: (fileFilters: Partial<FileFilters>) => {
        const currentFileFilters = get().fileFilters;
        const newFileFilters = { ...currentFileFilters, ...fileFilters };
        persistFileFilters(newFileFilters);
        set({ fileFilters: newFileFilters }, false, 'setFileFilters');
      },

      resetFileFilters: () => {
        const defaultFileFilters: FileFilters = {
          selectedSerial: 'All',
          startDate: null,
          endDate: null,
          sortBy: 'name',
          sortOrder: 'asc',
          includeUntagged: false,
        };
        persistFileFilters(defaultFileFilters);
        set({ fileFilters: defaultFileFilters }, false, 'resetFileFilters');
      },

      // State actions
      setActiveTab: (tab: DashboardTab) => {
        set({ activeTab: tab }, false, 'setActiveTab');
        // The folder id rides along, so entering Files writes
        // `/files/folder/<the folder you were last in>` and leaving writes `/`
        // WITHOUT losing it (`buildDashboardUrl` ignores it off the tab). The
        // folder therefore survives a tab switch, which it did not while it was
        // component state that died with the unmounted tab.
        syncUrlState(tab, get().selectedSerial, get().filesFolderId);
      },

      setFilesFolder: (folder: FolderRef, opts?: { replace?: boolean }) => {
        const { activeTab, selectedSerial, filesFolderId, filesFolderInvalid } = get();
        const target = folder.id;

        // Already here — no state write, no history entry. Clicking the crumb
        // for the folder you are in is not a navigation, and giving it an entry
        // makes the next Back press do nothing visible.
        //
        // The state alone decides, deliberately. Every path that sets it also
        // settles the url (a push here, a `replaceState` from the
        // canonicalizer, or the popstate read), so equal ids already mean the
        // address bar shows this node — possibly in its canonical ROOT spelling
        // `/files` rather than `/files/folder/<the root's own id>`, which a
        // string or route comparison would misread as a disagreement and
        // "fix" with a spurious entry.
        if (target === filesFolderId && !filesFolderInvalid) return;

        set({ filesFolderId: target, filesFolderInvalid: false }, false, 'setFilesFolder');
        const write = opts?.replace ? replaceUrlState : syncUrlState;
        write(activeTab, selectedSerial, target);
      },

      setHasAttemptedLoad: (value: boolean) => {
        set({ hasAttemptedLoad: value }, false, 'setHasAttemptedLoad');
      },

      // Auth actions (minimal)
      signOut: async () => {
        // Note: This should be called from Dashboard component which has access to auth.signOut
        // The store just manages the loading state
        set({ isLoggingOut: true }, false, 'signOut-start');
      },

      resetLogoutState: () => {
        set({ isLoggingOut: false }, false, 'resetLogoutState');
      },

      // Data actions
      setData: (data: Partial<DashboardData>) => {
        set(
          (state) => ({
            data: { ...state.data, ...data },
          }),
          false,
          'setData'
        );
      },

      setLoading: (loading: boolean) => {
        set({ loading }, false, 'setLoading');
      },

      setUpdating: (updating: boolean) => {
        set({ updating }, false, 'setUpdating');
      },

      setError: (error: string | null) => {
        set({ error }, false, 'setError');
      },

      // Complex actions — API integration (split out, STD-014)
      ...createDataActions(set, get),

      // Effects actions + serial/flight-type loaders (split out, STD-014)
      ...createEffectsActions(set, get),
    }),
    {
      name: 'dashboard-store',
      enabled: true,
    }
  )
);

// Make store accessible from browser console for debugging
if (typeof window !== 'undefined') {
  (window as Window & { useDashboardStore?: typeof useDashboardStore }).useDashboardStore =
    useDashboardStore;
}
